import { Outlet, Link, createRootRouteWithContext, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { installClickTracking, installBigDatesObserver, trackEvent } from "@/lib/analytics-events";
import { installPhoneCopyTracking, sendLeadEvent } from "@/lib/lead-tracking";
import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { captureAttribution } from "@/lib/attribution";

// Font @font-face declarations are inlined in src/styles.css and font files
// are preloaded below, no separate @fontsource CSS imports needed.

interface RouterContext {
  queryClient: QueryClient;
}

import appCss from "../styles.css?url";

// Preload critical above-the-fold font files (parallel with CSS, no chain)
import dmSans400 from "@fontsource/dm-sans/files/dm-sans-latin-400-normal.woff2?url";
import playfair600 from "@fontsource/playfair-display/files/playfair-display-latin-600-normal.woff2?url";
import playfair700 from "@fontsource/playfair-display/files/playfair-display-latin-700-normal.woff2?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          Page not found
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
       { title: "Sprinter Limo Service Atlanta | Atlanta Luxury Sprinters" },
       { name: "description", content: "Chauffeured Mercedes-Benz Sprinter limos in Atlanta for airport and private jet transfers, corporate travel, weddings and nights out. Call (678) 431-7222." },
      { name: "author", content: "Atlanta Luxury Sprinters" },
      { property: "og:title", content: "Sprinter Limo Service Atlanta | Atlanta Luxury Sprinters" },
      { name: "twitter:title", content: "Sprinter Limo Service Atlanta | Atlanta Luxury Sprinters" },
      { property: "og:description", content: "Chauffeured Mercedes-Benz Sprinter limos in Atlanta for airport and private jet transfers, corporate travel, weddings and nights out. Call (678) 431-7222." },
      { name: "twitter:description", content: "Chauffeured Mercedes-Benz Sprinter limos in Atlanta for airport and private jet transfers, corporate travel, weddings and nights out. Call (678) 431-7222." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/9c86e995-d897-4c9e-870b-90c382a845e9/id-preview-b913fa4d--9fe33457-1e5d-40da-94e1-fc7f9b119465.lovable.app-1777886113845.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/9c86e995-d897-4c9e-870b-90c382a845e9/id-preview-b913fa4d--9fe33457-1e5d-40da-94e1-fc7f9b119465.lovable.app-1777886113845.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { name: "google-site-verification", content: "8S1TNakdIfqX041TGcBZooDWy0Oh9WpUMdPBqv1bjyo" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "preload", as: "font", type: "font/woff2", href: playfair600, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: dmSans400, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: playfair700, crossOrigin: "anonymous" },
      { rel: "stylesheet", href: appCss },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){
try {
  if (window.sessionStorage.getItem('als_attribution')) return;
  var p = new URLSearchParams(window.location.search);
  var keys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'];
  var attr = {};
  keys.forEach(function(k){ var v = p.get(k); if (v) attr[k] = v.slice(0,255); });
  var click = p.get('gclid') || p.get('gbraid') || p.get('wbraid');
  if (click) attr.gclid = click.slice(0,255);
  var ref = p.get('ref') || p.get('ref_code');
  if (ref) attr.ref_code = ref.slice(0,80);
  attr.first_touch_at = new Date().toISOString();
  attr.landing_page = String(window.location.href).slice(0,500);
  if (document.referrer) attr.referrer = String(document.referrer).slice(0,500);
  window.sessionStorage.setItem('als_attribution', JSON.stringify(attr));
} catch (e) {}
})();`,
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-2QW56M101M');
(function(){
  var loaded = false;
  function load(){
    if (loaded) return; loaded = true;
    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=G-2QW56M101M';
    document.head.appendChild(s);
  }
  ['scroll','pointerdown','keydown','touchstart'].forEach(function(evt){
    window.addEventListener(evt, load, { once: true, passive: true });
  });
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(load, { timeout: 5000 });
  } else {
    window.addEventListener('load', function(){ setTimeout(load, 2500); });
  }
})();`,
          }}
        />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    captureAttribution();
    const offClicks = installClickTracking();
    const offBigDates = installBigDatesObserver();
    const offCopy = installPhoneCopyTracking(() => trackEvent("phone_copy"));
    // landing_view once per session, waiting briefly for the lazily loaded GA4 tag.
    let landingTimer: ReturnType<typeof setInterval> | undefined;
    try {
      if (!sessionStorage.getItem("als_landing_sent")) {
        sessionStorage.setItem("als_landing_sent", "1");
        let tries = 0;
        landingTimer = setInterval(() => {
          tries++;
          if (typeof (window as unknown as { gtag?: unknown }).gtag === "function" || tries > 20) {
            clearInterval(landingTimer);
            trackEvent("landing_view");
          }
        }, 500);
      }
    } catch { /* storage blocked */ }
    return () => {
      if (landingTimer) clearInterval(landingTimer);
      offCopy();
      offClicks();
      offBigDates();
    };
  }, []);

  // First-party page_view on every page, including client-side navigation, for the conversion report.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname.startsWith("/auth")) return;
    sendLeadEvent("page_view", { page_path: pathname });
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster />
    </QueryClientProvider>
  );
}
